import { effect, Injectable, Injector, untracked } from '@angular/core';
import { FormArray } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { K_LOCALE, Locale } from '@kiwid-app/kiwid-date-typescript';
import { HashHelper } from 'app/core/helpers/hash.helper';
import { IAddress } from 'app/core/models/address/address';
import QueryString from 'qs';
import { Observable } from 'rxjs';
import { MAX_PER_PAGE_PAGINATION } from '../const/pagination.const';
import { IMeta } from '../models/api/data';
import { IPagination } from '../models/api/pagination';
import { EAuthenticationStorage } from '../models/authentication/authentication';
import { ConvertHelper } from './convert.helper';

export type IObjectTypeParse<T extends keyof any> = Record<T, 'string' | 'number' | 'boolean' | 'array' | 'stringArray' | 'numberArray'>;

@Injectable()
export class Util {
    static getObjectQueryParamsByUrl(routerSourceType: string): object {
        if (routerSourceType.split('?').length > 1) {
            const parsed = QueryString.parse(routerSourceType.split('?')[1]);
            if (parsed) {
                Object.entries(parsed).forEach(([key, value]) => {
                    if (Array.isArray(value)) {
                        delete parsed[key];
                        parsed[`${key}[]`] = value;
                        return;
                    }

                    if (key === 'orderBy') {
                        const valueOrderBy = value;
                        delete parsed[key];

                        // example orderBy[order.number]=ASC
                        Object.entries(valueOrderBy).forEach(([keyOrder, valueOrder]) => {
                            parsed[`${key}[${keyOrder}]`] = valueOrder;
                        });
                    }
                });

                return parsed;
            }

            return {};
        }

        return {};
    }

    static translateKey(
        translate: any,
        key: string,
        params?: {
            [key: string]: string;
        },
        type?: 'one' | 'other' | 'zero',
    ): string {
        if (type) {
            key = `${key}.${type.toUpperCase()}`;
        }

        if (!translate) {
            return key;
        }

        const splitKey = key.split('.');
        let data = translate;
        for (const newKey of splitKey) {
            if (data[newKey]) {
                data = data[newKey];
                continue;
            }

            return key;
        }

        if (!!params) {
            Object.entries(params).forEach(([keyParam, valueParam]) => {
                data = (data as string).replace(`{{ ${keyParam} }}`, valueParam).replace(`{{${keyParam}}}`, valueParam);
            });
        }

        return data;
    }

    static uploadInputFile(lock = false): Observable<FileList> {
        return new Observable((observer) => {
            // create input file
            const el = document.createElement('input');
            el.id = HashHelper.uuidv4();
            el.style.display = 'none';
            el.setAttribute('type', 'file');
            document.body.appendChild(el);

            el.addEventListener(
                'change',
                () => {
                    lock = true;

                    observer.next(el.files);
                    observer.complete();
                    // remove dom
                    document.body.removeChild(document.getElementById(el.id));
                },
                { once: true },
            );

            // file blur
            window.addEventListener(
                'focus',
                () => {
                    setTimeout(() => {
                        if (!lock && document.getElementById(el.id)) {
                            document.body.removeChild(document.getElementById(el.id));
                            observer.error(new Error('onBlur'));
                            observer.complete();
                        }
                    }, 300);
                },
                { once: true },
            );

            // open file select box
            el.click();
        });
    }

    static parseQueryParams<Interface>(activatedRoute: ActivatedRoute, defaultData: Interface, cropExtraData = true): Interface {
        const queryParams = { ...activatedRoute.snapshot.queryParams };

        if (!!queryParams) {
            const temArrays: Record<string, any[]> = {};
            for (const [key, value] of Object.entries(queryParams)) {
                if (cropExtraData && !Object.entries(defaultData).some(([keyDefault]) => keyDefault === key)) {
                    continue;
                }

                if (key.indexOf('[]') > -1) {
                    if (Array.isArray(value)) {
                        if (temArrays[key] && Array.isArray(temArrays[key])) {
                            temArrays[key] = [...temArrays[key], ...value];
                        } else {
                            temArrays[key] = value;
                        }
                    } else {
                        if (temArrays[key] && Array.isArray(temArrays[key])) {
                            temArrays[key] = [...temArrays[key], value];
                        } else {
                            temArrays[key] = [value];
                        }
                    }
                    continue;
                }

                if (value === 'true' || value === 'false') {
                    defaultData[key] = ConvertHelper.toBoolean(value);
                    continue;
                }

                switch (typeof defaultData[key]) {
                    case 'boolean': {
                        defaultData[key] = ConvertHelper.toBoolean(value);
                        break;
                    }
                    case 'string': {
                        defaultData[key] = String(value);
                        break;
                    }
                    case 'number': {
                        defaultData[key] = Number(value);
                        break;
                    }
                    default: {
                        defaultData[key] = value ?? null;
                    }
                }

                if (key === 'perPage' && !!value && Number(value) > MAX_PER_PAGE_PAGINATION) {
                    defaultData[key] = MAX_PER_PAGE_PAGINATION;
                }
            }

            Object.entries(temArrays).forEach(([key, value]) => {
                defaultData[key] = value;
            });
        }

        return defaultData;
    }

    static currentLocale(): Locale {
        const currentLang = localStorage.getItem(EAuthenticationStorage.LANG);

        const locales = {
            pt: K_LOCALE.ptBR,
            es: K_LOCALE.es,
            en: K_LOCALE.enUS,
        };

        return locales[currentLang] ?? K_LOCALE.enUS;
    }

    static currentLang(): string {
        return localStorage.getItem(EAuthenticationStorage.LANG) ?? 'en';
    }

    static copyToClipboard(value: string): void {
        const selBox = document.createElement('textarea');
        selBox.style.position = 'fixed';
        selBox.style.left = '0';
        selBox.style.top = '0';
        selBox.style.opacity = '0';
        selBox.value = value;
        document.body.appendChild(selBox);
        selBox.focus();
        selBox.select();
        document.execCommand('copy');
        document.body.removeChild(selBox);
    }

    static isUnitary(measureId: number): boolean {
        return measureId === 1;
    }
}

export function isLastPage(meta: IMeta<IPagination>): boolean {
    return meta.pagination.count < meta.pagination.limit;
}

export function embedGoogleURL(domSanitizer: DomSanitizer, url: string): SafeUrl {
    return domSanitizer.bypassSecurityTrustResourceUrl(`https://docs.google.com/gview?embedded=true&url=${url}`);
}

export function generateFullAddress(address: IAddress): string {
    const { street, number, complement, district, city, state, country } = address;

    return `${street ?? ''}${number ? `, ${number}` : ''}${complement ? ` - ${complement}` : ''}${district ? ` - ${district}` : ''}${
        city ? `, ${city}` : ''
    }${state ? ` - ${state}` : ''}${country ? `, ${country}` : ''}`;
}

export function arrayGroupBy<K, V>(list: Array<V>, keyGetter: (input: V) => K): { key: K; value: V[] }[] {
    const map: { key: K; value: V[] }[] = [];

    list.forEach((item) => {
        const key = keyGetter(item);
        const idx = map.findIndex((itemFind) => itemFind.key === key);
        if (idx > -1) {
            map[idx].value.push(item);
        } else {
            map.push({ key: keyGetter(item), value: [item] });
        }
    });
    return map;
}

export function uniqueArray<T>(array: T[]): T[] {
    return [...new Set(array)];
}

export const clearFormArray = (formArray: FormArray) => {
    while (formArray.length !== 0) {
        formArray.removeAt(0);
    }
};

export function parseStringToSearch(search: string) {
    if (!search) {
        return '';
    }

    return String(search)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
}

export function validateBarcodeProduct(cod: string): boolean {
    function validateEAN13(code: string) {
        if (!/^\d{13}$/.test(code)) {
            return false;
        }

        const digits = code.split('').map(Number);
        let sum = 0;
        for (let i = 0; i < 12; i += 2) {
            sum += digits[i];
        }

        for (let i = 1; i < 12; i += 2) {
            sum += digits[i] * 3;
        }

        const checkDigit = (10 - (sum % 10)) % 10;

        return digits[12] === checkDigit;
    }

    function validateUPCA(code: string) {
        if (!/^\d{12}$/.test(code)) {
            return false;
        }

        const digits = code.split('').map(Number);
        let sum = 0;
        for (let i = 0; i < 11; i += 2) {
            sum += digits[i] * 3;
        }

        for (let i = 1; i < 11; i += 2) {
            sum += digits[i];
        }

        const checkDigit = (10 - (sum % 10)) % 10;

        return digits[11] === checkDigit;
    }

    return validateEAN13(cod) || validateUPCA(cod);
}

export function normalizeSpaces(str: string): string {
    if (!str) {
        return null;
    }

    return str.replace(/\s+/g, ' ').trim();
}

export interface BlurConfig {
    enabled: boolean;
    selectors: string[];
    blurClass: string;
}

export function effectBlur(computedSignal: () => BlurConfig, options: { injector: Injector; onCleanup?: () => void }): void {
    effect(
        (onCleanup) => {
            const config = computedSignal();
            const { enabled, selectors, blurClass } = config;

            untracked(() => {
                selectors.forEach((selector) => {
                    const elements = document.querySelectorAll<HTMLDivElement>(selector);

                    elements.forEach((element) => {
                        if (element) {
                            let overlayDiv = element.querySelector('.overlay-div') as HTMLDivElement;

                            if (enabled) {
                                if (!element.classList.contains(blurClass)) {
                                    element.classList.add(blurClass);
                                }

                                if (!overlayDiv) {
                                    overlayDiv = document.createElement('div');
                                    overlayDiv.className = 'overlay-div absolute top-0 right-0 bottom-0 left-0 w-full h-full';
                                    element.appendChild(overlayDiv);
                                }
                            } else {
                                if (element.classList.contains(blurClass)) {
                                    element.classList.remove(blurClass);
                                }

                                if (overlayDiv) {
                                    overlayDiv.remove();
                                }
                            }
                        }
                    });
                });
            });

            onCleanup(() => options.onCleanup?.());
        },
        { injector: options.injector },
    );
}

interface ImageDimension {
    width: number;
    height: number;
    maxWidth?: number;
    maxHeight?: number;
    minWidth?: number;
    minHeight?: number;
}

interface ProportionalDimension {
    width: number;
    height: number;
}

export function calculateProportionalDimensions(data: ImageDimension): ProportionalDimension {
    const { width, height, maxWidth, maxHeight, minWidth, minHeight } = data;

    if (!maxWidth && !maxHeight && !minWidth && !minHeight) {
        return { width, height };
    }

    let ratio = 1;

    if (maxWidth && maxHeight) {
        const widthRatio = maxWidth / width;
        const heightRatio = maxHeight / height;
        ratio = Math.min(widthRatio, heightRatio);
    } else if (maxWidth) {
        ratio = maxWidth / width;
    } else if (maxHeight) {
        ratio = maxHeight / height;
    }

    ratio = Math.min(ratio, 1);

    let newWidth = width * ratio;
    let newHeight = height * ratio;

    if (minWidth || minHeight) {
        let minRatio = 1;

        if (minWidth && minHeight) {
            const minWidthRatio = minWidth / newWidth;
            const minHeightRatio = minHeight / newHeight;
            minRatio = Math.max(minWidthRatio, minHeightRatio);
        } else if (minWidth) {
            minRatio = minWidth / newWidth;
        } else if (minHeight) {
            minRatio = minHeight / newHeight;
        }

        if (minRatio > 1) {
            newWidth *= minRatio;
            newHeight *= minRatio;
        }
    }

    return {
        width: newWidth,
        height: newHeight,
    };
}
